import LoadingSpinner from '@carvana/loading-spinner';
import { useEffectOnce, usePromise } from '@carvana/merch-react-hooks';
import Input from '@carvana/showroom-forms/Input';
import Email from '@carvana/showroom-forms/Input/Email';
import { type ToastOptions } from '@carvana/showroom-toast';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { PrimaryButton } from '@/common/Button';
import useAlertName, { nameLimit } from '@/hooks/srp/useAlertName';
import {
  useIsLoggedIn,
  useUserEmail,
  useUserId,
  useUserLocation
} from '~/context/common/UserContext';
import AppliedFiltersContext from '~/context/srp/AppliedFiltersContext';
import FacetDataContext from '~/context/srp/FacetDataContext';
import ModalContext from '~/context/srp/ModalContext';
import SavedSearchesContext from '~/context/srp/SavedSearchesContext';
import { useTrackEvent } from '~/context/srp/SearchAnalyticsContext';
import verifyEmail, { EmailStatus } from '~/services/common/verifyEmail';
import { createSavedSearch } from '~/services/srp/savedSearch';
import { logError } from '~/utilities/loggers';
import { stringifyAndEncode } from '~/utilities/srp/crypto';
import sanitizeString from '~/utilities/srp/Validation/sanitizeString';
import useGetFilterTags from '../../MainLayout/useGetFilterTags';

function useFieldChanged(fieldName: string) {
  const [didFieldChange, setFieldChanged] = useState(false);
  const trackEvent = useTrackEvent();

  useEffectOnce(
    () => {
      trackEvent(`Save Search Nudge - Changed ${fieldName}`);
    },
    [fieldName, trackEvent],
    didFieldChange
  );

  return setFieldChanged;
}

type OwnProps = {
  showToast: (opt: ToastOptions) => void;
};

export default function SaveSearchNudgeForm({ showToast }: OwnProps) {
  const { appliedFilters } = useContext(AppliedFiltersContext);
  const { isSaveSearchNudgeOpen, setSaveSearchNudgeOpen } =
    useContext(ModalContext);
  const { triggerFetchSavedSearches } = useContext(SavedSearchesContext);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setLoading] = useState(false);
  const isLoggedIn = useIsLoggedIn();
  const mounted = usePromise();
  const trackEvent = useTrackEvent();
  const userId = useUserId();
  const { zip } = useUserLocation();

  const [emailAddress, setEmailAddress] = useState(useUserEmail());
  const { baseline } = useContext(FacetDataContext);
  const filterTags = useGetFilterTags(
    baseline,
    appliedFilters,
    isSaveSearchNudgeOpen
  );
  const [alertName, setAlertName] = useAlertName(filterTags);

  const setEmailChanged = useFieldChanged('email address');
  const setNameChanged = useFieldChanged('name');

  const onSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
    async ev => {
      ev.preventDefault();

      try {
        setErrors({});

        let isValid = true;
        const formData = new FormData(ev.currentTarget);

        const email = formData.get('email');
        const name = formData.get('name');

        if (!email) {
          isValid = false;
          setErrors(v => ({ ...v, email: 'Email is required' }));
          trackEvent('Save Search Nudge - Email is required');
        }

        if (!name) {
          isValid = false;
          setErrors(v => ({ ...v, name: 'Name is required' }));
          trackEvent('Save Search Nudge - Name is required');
        }

        if (String(name).length > nameLimit) {
          isValid = false;
          setErrors(v => ({ ...v, name: `Maximum ${nameLimit} characters` }));
          trackEvent('Save Search Nudge - Name is too long', { name });
        }

        if (!isValid) return;

        setLoading(true);

        const verification = await verifyEmail(String(email));

        if (verification.status === EmailStatus.Invalid) {
          trackEvent('Save Search Nudge - Invalid Email', {
            ...verification,
            email
          });

          isValid = false;
          setErrors(v => ({
            ...v,
            email: verification.detail ?? 'Email is invalid'
          }));
        }

        if (!isValid) {
          setLoading(false);

          return;
        }

        await mounted(
          createSavedSearch({
            email: String(email),
            name: sanitizeString(String(name)),
            url: stringifyAndEncode({ filters: appliedFilters }),
            userId,
            zip5: zip
          })
        );

        setLoading(false);

        trackEvent('Save Search Nudge - Submitted');

        showToast({
          label: 'We saved your search!',
          lifetime: 3000,
          severity: 'success'
        });

        setSaveSearchNudgeOpen(false);
        triggerFetchSavedSearches();
      } catch (err) {
        setErrors(v => ({ ...v, general: 'An error occurred' }));

        logError(err, { location: 'Save Search Nudge' });

        setLoading(false);
      }
    },
    [
      appliedFilters,
      mounted,
      setSaveSearchNudgeOpen,
      showToast,
      trackEvent,
      triggerFetchSavedSearches,
      userId,
      zip
    ]
  );

  useEffect(() => {
    setErrors({});
  }, [isSaveSearchNudgeOpen]);

  return (
    <form className="grid gap-16" onSubmit={onSubmit}>
      <div className="grid gap-8">
        <Input
          id="save-search-name"
          data-qa="save-search-name"
          name="name"
          type="text"
          label="Your Search"
          value={alertName}
          onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
            setAlertName(ev.currentTarget.value);
            setNameChanged(true);
          }}
          onClear={() => {
            setAlertName('');
            setNameChanged(true);
          }}
          error={errors.name}
          touched={Boolean(errors.name)}
        />

        <Email
          id="save-search-email"
          data-qa="save-search-email"
          label="Email"
          disabled={isLoggedIn}
          value={emailAddress}
          onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
            setEmailAddress(ev.currentTarget.value);
            setEmailChanged(true);
          }}
          onClear={() => {
            setEmailAddress('');
            setNameChanged(true);
          }}
          error={errors.email}
          touched={Boolean(errors.email)}
        />

        <input name="email" type="hidden" value={emailAddress} />
      </div>

      <div className="grid gap-8">
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <PrimaryButton
            type="submit"
            data-analytics-id="Save Search Nudge - Submit"
          >
            Save Your Search
          </PrimaryButton>
        )}

        {errors.general ? (
          <p className="text-red-2 tl-body-xs">{errors.general}</p>
        ) : null}
      </div>
    </form>
  );
}
